import React, { useState, useMemo, useCallback, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import {
    Tabs,
    TabList,
    TabContent,
    Paragraph,
    Collapsible,
    Button,
    TrashIcon,
    Table,
    Badge,
    DetailedAlertMessages,
    ModalMultiUse,
} from '@sentara-health/optimahealth_web';
import { DesignatedRepresentativeProps } from './interface';
import { ProfileHeaderContent } from 'templates';
import { useDeviceType, DeviceTypes, NavigateConst, listType, useProxyAuthorization, useProxyRead, formatISODateToMDY, logoutErrorHandler, ANALYTICS_MESSAGES, domainForSegment, isUnAuthorizedError, unAuthorizedErrorHandler } from 'common';
import { ProxyInfo, useSendProxy, useConfirmProxy, ProxyAuthorizationRequestStatus } from '@sentara-health/sentara-api-hooks-core';
import { GlobalContext } from 'context';

type ActionButtonConfig = {
    text: string;
    variant: string;
    textColor: string;
    iconLeft: React.ReactNode;
    iconStroke: string;
    disabled?: boolean;
    onClick: () => void;
};

type RepresentativeItem = {
    name: string;
    actionKeys: string[];
    [key: string]: unknown;
};

const getSerializableRepData = (repData?: RepresentativeItem) => {
    if (!repData) {
        return undefined;
    }

    const { status: _status, actions: _actions, ...rest } = repData;
    return rest;
};

const EXCLUDED_REP_KEYS = new Set(['actionKeys', 'timestamp', 'attemptsRemaining', 'designatorLoginId', 'correlationId', 'designateLoginId', 'email', 'claimsList', 'expirationStartRaw', 'expirationEndRaw', 'disclosureOption', 'disclosureOptionDescription', 'alcoholAbuseAuthorization']);
const EXCLUDED_ACCORDION_KEYS = new Set(['actions', 'actionKeys', 'timestamp', 'attemptsRemaining', 'designatorLoginId', 'correlationId', 'designateLoginId', 'email', 'claimsList', 'expirationStartRaw', 'expirationEndRaw', 'disclosureOption', 'disclosureOptionDescription', 'alcoholAbuseAuthorization']);
const noop = () => { };
const normalizeRepName = (value?: string) => (value ?? '').trim().replaceAll(/\s+/g, ' ').toLowerCase();

// Read API returns consents as an array of single-key objects; flatten into one object.
const mergeConsents = (consents: Array<Record<string, unknown>> = []): Record<string, unknown> =>
    consents.reduce((acc, entry) => ({ ...acc, ...entry }), {} as Record<string, unknown>);

// Information Shared: the four disclosure authorization categories; when all are present, collapse to a single label.
const DISCLOSURE_AUTH_KEYS = ['checkBoxOption1', 'checkBoxOption2', 'checkBoxOption3', 'checkBoxOption4'] as const;
const formatInfoShared = (disclosureList: string[]): string => {
    const normalized = new Set(disclosureList.map((item) => item.trim().toLowerCase()));
    // Claims from the API are always English, so match against the English labels regardless of the active language.
    const hasAllDisclosures = DISCLOSURE_AUTH_KEYS.every((key) =>
        normalized.has(t(`profile.designatedRepresentative.${key}`, { lng: 'en' }).trim().toLowerCase())
    );
    return hasAllDisclosures ? t('profile.designatedRepresentative.allInformation') : disclosureList.join(', ');
};

// Accounts Shared With Me confirmationDate arrives as "MM/DD/YYYY HH:mm:ss +00:00"; keep only the MDY date part.
const formatSharedApproveDate = (dateString?: string) => {
    if (!dateString) {
        return '';
    }
    return dateString.includes('T') ? formatISODateToMDY(dateString) : dateString.split(' ')[0];
};

// My Designated Representatives: label shown in the status Badge per API (proxyAuthorizationRequestStatus) value.
const MY_REP_STATUS_DISPLAY: Record<string, string> = {
    Approved: 'Accepted',
    Denied: 'Denied',
    Pending: 'Pending',
};

// My Designated Representatives: statuses that must not appear in the list.
const MY_REP_HIDDEN_STATUSES = new Set(['Terminated', 'TokenExpired', 'Expired']);

// My Designated Representatives: row actions per API status.
const STATUS_ACTION_KEYS: Record<string, string[]> = {
    Approved: ['update', 'remove'],
    Denied: ['resend'],
    Pending: [],
};

// Accounts Shared With Me: row actions per API status; statuses absent here (e.g. Terminated/Denied) are hidden entirely.
const SHARED_STATUS_ACTION_KEYS: Record<string, string[]> = {
    Approved: ['remove'],
    Pending: ['accept', 'deny'],
};

const MEMBER_VIEW_EMPTY_APPROVE_DATE_STATUSES = new Set(['Denied']);
const PROXY_VIEW_EMPTY_APPROVE_DATE_STATUSES = new Set(['Pending', 'Denied']);



const normalizeProxyStatus = (status?: string): string => {
    const normalized = (status ?? '').trim().toLowerCase();

    switch (normalized) {
        case 'approved':
            return 'Approved';
        case 'pending':
            return 'Pending';
        case 'denied':
            return 'Denied';
        case 'terminated':
            return 'Terminated';
        case 'tokenexpired':
            return 'TokenExpired';
        case 'expired':
            return 'Expired';
        default:
            return status ?? '';
    }
};

const getIdentifierBySystem = (
    identifiers: Array<{ system?: string; value?: string }> = [],
    targetSystem: string
): string => {
    const normalizedTarget = targetSystem.trim().toLowerCase();
    const match = identifiers.find(
        (id) => (id.system ?? '').trim().toLowerCase() === normalizedTarget
    );
    return match?.value ?? '';
};

// Table header keys whose translation lives under profile.designatedRepresentative instead of common.
const HEADER_TRANSLATION_OVERRIDES: Record<string, string> = {
    name: 'profile.name',
    infoCanAccessWeb: 'profile.designatedRepresentative.infoCanAccessWeb',
    dateApproved: 'profile.designatedRepresentative.dateApprovedWeb',
    actions: 'profile.designatedRepresentative.actions',
};

const resolveHeaderTranslationKey = (key: string) => HEADER_TRANSLATION_OVERRIDES[key] ?? `common.${key}`;

const shouldIncludeHeaderKey = (key: string, isDesktop: boolean) =>
    !EXCLUDED_REP_KEYS.has(key) && (isDesktop || key !== 'actions');

const shouldIncludeAccordionKey = (key: string) => !EXCLUDED_ACCORDION_KEYS.has(key);

const getAckResendCount = (value: unknown): number | undefined => {
    if (value === null || value === undefined) {
        return undefined;
    }

    if (Array.isArray(value)) {
        for (const item of value) {
            const count = getAckResendCount(item);
            if (count !== undefined) {
                return count;
            }
        }
        return undefined;
    }

    if (typeof value !== 'object') {
        return undefined;
    }

    const objectValue = value as Record<string, unknown>;
    const directRaw = objectValue.acknowledgementRequestResendCount
        ?? objectValue.acknowledgmentRequestResendCount;
    const direct = Number(directRaw);

    if (!Number.isNaN(direct)) {
        return direct;
    }

    for (const child of Object.values(objectValue)) {
        const count = getAckResendCount(child);
        if (count !== undefined) {
            return count;
        }
    }

    return undefined;
};

export const renderActionButtons = (
    actions: string[],
    actionsBtns: Record<string, ActionButtonConfig>,
    isDesktop: boolean
) => {
    if (!actions.length) {
        return <div className="text-left">-</div>;
    }

    const buttons = actions.reduce<
        Array<{
            item: string;
            element: JSX.Element;
        }>
    >((acc, item, index) => {
        const btn = actionsBtns[item];

        if (!btn) {
            return acc;
        }

        acc.push({
            item,
            element: (
                <Button
                    key={`action-btn-${item}-${index}`}
                    dataTestId={`${item}-btn`}
                    variant={btn.variant}
                    onClick={btn.onClick}
                    disabled={btn.disabled}
                    iconLeft={btn.iconLeft}
                    textColor={btn.textColor}
                    iconStroke={btn.iconStroke}
                    className="btn-sm-height mb-3"
                >
                    {btn.text}
                </Button>
            ),
        });

        return acc;
    }, []);

    if (!buttons.length) {
        return <div className="text-left">-</div>;
    }

    if (isDesktop) {
        return (
            <>
                {buttons.map((btn, index) => (
                    <div key={`action-wrapper-${index}`} className="col-12">
                        {btn.element}
                    </div>
                ))}
            </>
        );
    }

    return (
        <div className="row">
            {buttons.map((btn, index) => (
                <div
                    key={`action-wrapper-mobile-${index}`}
                    className="col-8 col-sm-4"
                >
                    {btn.element}
                </div>
            ))}
        </div>
    );
};

const DesignatedRepresentative = ({
    expand = true,
    onExpand,
    onCollapse,
    designatedRepsData,
    accountSharedRepsData,
    showErrorOverride = false,
    viewMode = 'full',
    showCollapsible = true,
    onSharedActionComplete,
}: DesignatedRepresentativeProps) => {
    const [initialLoader, setInitialLoader] = useState(false);
    const [actionLoader, setActionLoader] = useState(false);
    const [showError] = useState<boolean>(showErrorOverride); // work in progress
    const [isActiveTab, setIsActiveTab] = useState<string>('tab1');
    const [resendAttemptsByRep, setResendAttemptsByRep] = useState<Record<string, number>>({});
    const [showResendAttemptsAlert, setShowResendAttemptsAlert] = useState<boolean>(false);
    const [selectedResendRepName, setSelectedResendRepName] = useState<string>('');
    const [isRemoveDesignatedRepModalOpen, setIsRemoveDesignatedRepModalOpen] = useState<boolean>(false);
    const [selectedRemoveRepName, setSelectedRemoveRepName] = useState<string>('');
    const [selectedRemoveDesignatorLoginId, setSelectedRemoveDesignatorLoginId] = useState<string>('');
    const [selectedRemoveCorrelationId, setSelectedRemoveCorrelationId] = useState<string>('');
    const [selectedRemoveDesignateLoginId, setSelectedRemoveDesignateLoginId] = useState<string>('');
    const [removeFlowType, setRemoveFlowType] = useState<'designatedRep' | 'sharedAccount'>('designatedRep');
    const [removedRepNames, setRemovedRepNames] = useState<string[]>([]);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successModalMessage, setSuccessModalMessage] = useState('');
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorModalTitle, setErrorModalTitle] = useState('');
    const [errorModalMessage, setErrorModalMessage] = useState('');
    const navigate = useNavigate();
    const deviceType = useDeviceType();
    const isMobile = deviceType === DeviceTypes.MOBILE;
    const isDesktop = deviceType === DeviceTypes.DESKTOP;
    const { terminateProxy } = useProxyAuthorization();
    const { getProxyRead } = useProxyRead();
    const { contentListingItemClicked } = useAdobeAnalytics();
    const { sendProxyAuthorization } = useSendProxy();
    const { confirmProxyAuthorization } = useConfirmProxy();
    const { setApprovedDesignators, setSharedAccessRefreshKey } = useContext(GlobalContext);

    const refreshGuestApprovalState = useCallback(async () => {
        try {
            const readResponse = await getProxyRead();
         
            if (readResponse?.status !== 200) {
                setApprovedDesignators([]);
                return;
            }

            const proxies = (readResponse?.data?.proxies ?? []) as Array<Record<string, unknown>>;
            const approved = proxies.reduce(
                (acc: Array<{ name: string; status: string }>, proxy) => {
                    const consents = (proxy?.Consents ?? proxy?.consents) as Array<Record<string, unknown>> | undefined;
                    const mergedConsent = (consents ?? []).reduce(
                        (consentAcc, entry) => ({ ...consentAcc, ...entry }),
                        {} as Record<string, unknown>
                    );

                    const designationStatus =
                        (mergedConsent?.designationStatus as string | undefined) ?? '';
                    const status =
                        ((mergedConsent?.proxyAuthorizationRequestStatus ?? mergedConsent?.ProxyAuthorizationRequestStatus) as string | undefined) ?? '';

                    if (
                        designationStatus.toLowerCase() !== 'designate' ||
                        status.toLowerCase() !== 'approved'
                    ) {
                        return acc;
                    }

                    const humanName = proxy.humanName as { text?: string; given?: string[]; family?: string } | undefined;
                    const name =
                        humanName?.text?.trim() ||
                        `${humanName?.given?.[0] ?? ''} ${humanName?.family ?? ''}`.trim();

                    if (name) {
                        acc.push({ name, status });
                    }

                    return acc;
                },
                []
            );

            setApprovedDesignators(approved);
        } catch {
            setApprovedDesignators([]);
        }
    }, [getProxyRead, setApprovedDesignators]);

    const [proxyList, setProxyList] = useState<ProxyInfo[]>([]);
    const hasInitialized = React.useRef(false);

    const extractErrorCode = useCallback((error: unknown): string | undefined => {
        if (typeof error === 'string') {
            return error.match(/\b(401|440)\b/)?.[1];
        }

        return undefined;
    }, []);

    const getErrorMessage = useCallback((error: unknown) => {
        if (typeof error === 'string' && error.trim()) {
            return error;
        }

        if (error && typeof error === 'object' && 'message' in error) {
            const message = (error as { message?: string }).message;

            if (message && message.trim()) {
                return message;
            }
        }

        return t('common.internalServerError') || '';
    }, []);

    const isNotFoundError = useCallback((error: unknown): boolean => {
        if (typeof error === 'string') {
            return /\b404\b/.test(error);
        }

        if (error && typeof error === 'object') {
            if ('errorCode' in error) {
                const errorCode = (error as { errorCode?: unknown }).errorCode;

                if (`${errorCode}` === '404') {
                    return true;
                }
            }

            if ('message' in error) {
                const message = (error as { message?: string }).message;

                if (message && /\b404\b/.test(message)) {
                    return true;
                }
            }
        }

        return false;
    }, []);

    const openErrorModal = useCallback((error: unknown, title?: string) => {
        const errorCode = extractErrorCode(error);

        if (logoutErrorHandler(errorCode ?? error)) {
            return;
        }

        setErrorModalTitle(title || t('common.error'));
        setErrorModalMessage(getErrorMessage(error));
        setShowErrorModal(true);
    }, [extractErrorCode, getErrorMessage]);

    const openDesignatedRepLoadErrorModal = useCallback(() => {
        setErrorModalTitle(t('profile.designatedRepresentative.apiFailureTitle'));
        setErrorModalMessage(t('profile.designatedRepresentative.designatedRepLoadFailedMessage'));
        setShowErrorModal(true);
    }, []);

    const closeErrorModal = useCallback(() => {
        setShowErrorModal(false);
        setErrorModalTitle('');
        setErrorModalMessage('');
    }, []);

    const closeSuccessModal = useCallback(() => {
        setShowSuccessModal(false);
        setSuccessModalMessage('');
    }, []);

    const trackDrContentClick = useCallback((
        config: (typeof ANALYTICS_MESSAGES.DR_CONTENT_LISTING)[keyof typeof ANALYTICS_MESSAGES.DR_CONTENT_LISTING]
    ) => {
        contentListingItemClicked({
            segment: `${domainForSegment}/${globalThis.location.pathname}`,
            contentListing: {
                eventType: 'Content Listing Item Clicked',
                listing: {
                    content: [{ ...config }],
                },
            },
        });
    }, [contentListingItemClicked]);

    const handleClickAdd = useCallback((repData?: RepresentativeItem) => {
        trackDrContentClick(
            repData
                ? ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_UPDATE_CLICK
                : ANALYTICS_MESSAGES.DR_CONTENT_LISTING.ADD_DESIGNATED_REP_CLICK
        );
        const serializableRepData = getSerializableRepData(repData);
        navigate(
            NavigateConst.csrProxyForm,
            serializableRepData ? { state: { repData: serializableRepData } } : undefined
        );
    }, [navigate, trackDrContentClick]);

    const getAttemptsForRep = useCallback(
        (repName: string) => resendAttemptsByRep[repName] ?? 3,
        [resendAttemptsByRep]
    );

    const getAttemptsLeftFromResendResponse = useCallback((response: unknown, correlationId?: string) => {
        const payload = response as {
            proxies?: Array<Record<string, unknown>>;
            proxy?: Record<string, unknown>;
            data?: { proxies?: Array<Record<string, unknown>> };
        } | undefined;

        const proxies = payload?.proxies ?? payload?.data?.proxies ?? (payload?.proxy ? [payload.proxy] : []);

        const matchedProxy = correlationId
            ? proxies.find((proxy) => {
                const identifiers = (proxy?.identifier as Array<{ system?: string; value?: string }> | undefined) ?? [];
                return identifiers.some((id) => id.system === 'correlationId' && id.value === correlationId);
            })
            : undefined;

        const matchedCount = getAckResendCount(matchedProxy);
        if (matchedCount !== undefined) {
            return Math.max(0, 3 - matchedCount);
        }

        const firstProxy = proxies[0];
        const firstCount = getAckResendCount(firstProxy);
        if (firstCount !== undefined) {
            return Math.max(0, 3 - firstCount);
        }

        const payloadCount = getAckResendCount(payload);
        if (payloadCount !== undefined) {
            return Math.max(0, 3 - payloadCount);
        }

        return undefined;
    }, []);

    const getAttemptsLeftFromReadResponse = useCallback((
        response: unknown,
        correlationId: string
    ) => {

        const payload = response as {
            data?: { proxies?: Array<Record<string, unknown>> };
            proxies?: Array<Record<string, unknown>>;
        } | undefined;

        const proxies = payload?.data?.proxies ?? payload?.proxies ?? [];

        const matchedProxy = proxies.find((proxy) => {
            const identifiers = (proxy?.identifier as Array<{ system?: string; value?: string }> | undefined) ?? [];
            return identifiers.some((id) => id.system === 'correlationId' && id.value === correlationId);
        });

        if (!matchedProxy) {
            return undefined;
        }

        const resendCount = getAckResendCount(matchedProxy);
        if (resendCount !== undefined) {
            return Math.max(0, 3 - resendCount);
        }

        return undefined;
    }, []);

    const handleResendClick = useCallback(async (repName: string, correlationId?: string) => {
        if (!repName || !correlationId) {
            return;
        }

        const isUnauthorized = (value: unknown) =>
            typeof isUnAuthorizedError === 'function' && isUnAuthorizedError(String(value));
        const handleUnauthorized = () => {
            if (typeof unAuthorizedErrorHandler === 'function') {
                unAuthorizedErrorHandler();
            }
        };

        trackDrContentClick(ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_RESEND_CLICK);

        setActionLoader(true);

        try {
            const resendResponse = await sendProxyAuthorization({
                correlationId,
                identifier: [
                    {
                        system: 'correlationId',
                        value: correlationId
                    }
                ]
            });

            const responseStatus = Number((resendResponse as { status?: number | string } | undefined)?.status);
            const responseErrorCode = Number((resendResponse as { errorCode?: number | string } | undefined)?.errorCode);

             if (isUnauthorized(responseStatus)) {
                handleUnauthorized();
                return;
            }

            if (responseStatus === 409 || responseErrorCode === 409) {
                setSelectedResendRepName(repName);
                setShowResendAttemptsAlert(true);
                setResendAttemptsByRep((prev) => ({
                    ...prev,
                    [repName]: 0,
                }));
                return;
            }

            if (!Number.isNaN(responseStatus) && (responseStatus < 200 || responseStatus >= 300)) {
                openErrorModal(
                    (resendResponse as { message?: string } | undefined)?.message ||
                    t('common.internalServerError')
                );
                return;
            }

            const attemptsLeftFromSend = getAttemptsLeftFromResendResponse(resendResponse, correlationId);

            const readResponse = await getProxyRead();
            if (readResponse?.status === 200 || readResponse?.status === 204) {
                setProxyList(readResponse?.data?.proxies ?? []);
            }

            const attemptsLeftFromRead = getAttemptsLeftFromReadResponse(readResponse, correlationId);
            const resolvedAttemptsLeft = attemptsLeftFromRead ?? attemptsLeftFromSend;

            setSelectedResendRepName(repName);
            setShowResendAttemptsAlert(true);

            if (resolvedAttemptsLeft !== undefined) {
                setResendAttemptsByRep((prev) => ({
                    ...prev,
                    [repName]: resolvedAttemptsLeft,
                }));
            }

            contentListingItemClicked({
                segment: `${domainForSegment}/${globalThis.location.pathname}`,
                contentListing: {
                    eventType: 'Content Listing Item Clicked',
                    listing: {
                        content: [
                            {
                                contentId: ANALYTICS_MESSAGES.POP_AUTHORIZATION_CONSENT,
                                contentTitle: 'Resend Authorization',
                                itemPosition: 1,
                            },
                        ],
                    },
                },
            });
        } catch (error) {
            const errorStatus = Number(
                (error as { status?: number | string; errorCode?: number | string } | undefined)?.status
                ?? (error as { status?: number | string; errorCode?: number | string } | undefined)?.errorCode
            );
            const errorCode = Number((error as { errorCode?: number | string } | undefined)?.errorCode);

            if (isUnauthorized(errorStatus)) {
                handleUnauthorized();
                return;
            }

            if (errorStatus === 409 || errorCode === 409) {
                setSelectedResendRepName(repName);
                setShowResendAttemptsAlert(true);
                setResendAttemptsByRep((prev) => ({
                    ...prev,
                    [repName]: 0,
                }));
                return;
            }

            openErrorModal(error);
        } finally {
            setActionLoader(false);
        }
    }, [sendProxyAuthorization, contentListingItemClicked, trackDrContentClick, openErrorModal, getAttemptsLeftFromResendResponse, getAttemptsLeftFromReadResponse, getProxyRead]);

    // open the remove-confirmation modal for the given rep
    const handleRemoveClick = useCallback((repName: string, flowType: 'designatedRep' | 'sharedAccount' = 'designatedRep', designatorLoginId = '', correlationId = '', designateLoginId = '') => {
        if (!repName) {
            return;
        }
        trackDrContentClick(
            flowType === 'sharedAccount'
                ? ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCOUNT_SHARED_WITH_ME_REMOVE_CLICK
                : ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_REMOVE_CLICK
        );
        setSelectedRemoveRepName(repName);
        setSelectedRemoveDesignatorLoginId(designatorLoginId);
        setSelectedRemoveCorrelationId(correlationId);
        setSelectedRemoveDesignateLoginId(designateLoginId);
        setRemoveFlowType(flowType);
        setIsRemoveDesignatedRepModalOpen(true);
    }, [trackDrContentClick]);

    const onCloseRemove = useCallback(() => {
        contentListingItemClicked({
            segment: `${domainForSegment}/${globalThis.location.pathname}`,
            contentListing: {
                eventType: 'Content Listing Item Clicked',
                listing: {
                    content: [
                        {
                            contentId: ANALYTICS_MESSAGES.POP_AUTHORIZATION_KEEP_ACCESS,
                            contentTitle: 'Keep Access',
                            itemPosition: 1,
                        },
                    ],
                },
            },
        });

        setIsRemoveDesignatedRepModalOpen(false);

        setSelectedRemoveRepName('');
        setSelectedRemoveDesignatorLoginId('');
        setSelectedRemoveCorrelationId('');
        setSelectedRemoveDesignateLoginId('');
        setRemoveFlowType('designatedRep');
    }, [contentListingItemClicked]);

    // Load the logged-in user's proxy entitlements (all statuses) for the designated rep list.
    const loadProxyEntitlements = useCallback(async () => {
        setInitialLoader(true);
        try {
            const response = await getProxyRead();
            if (response.status !== 200 && response.status !== 204) {
                setProxyList([]);
                openDesignatedRepLoadErrorModal();
                return;
            }
            setProxyList(response.data?.proxies ?? []);
        } catch (error) {
            setProxyList([]);
            openDesignatedRepLoadErrorModal();
        } finally {
            setInitialLoader(false);
        }
    }, [getProxyRead, openDesignatedRepLoadErrorModal]);

    useEffect(() => {
        if (hasInitialized.current) {
            return;
        }
        hasInitialized.current = true;
        loadProxyEntitlements();
    }, [loadProxyEntitlements]);

    const onConfirmRemove = useCallback(async () => {
        contentListingItemClicked({
            segment: `${domainForSegment}/${globalThis.location.pathname}`,
            contentListing: {
                eventType: 'Content Listing Item Clicked',
                listing: {
                    content: [
                        {
                            contentId: ANALYTICS_MESSAGES.POP_AUTHORIZATION_REMOVE_ACCESS,
                            contentTitle: 'Remove Access',
                            itemPosition: 1,
                        },
                    ],
                },
            },
        });

        const repNameToRemove = selectedRemoveRepName;
        const currentRemoveFlowType = removeFlowType;
        const designatorLoginId = selectedRemoveDesignatorLoginId;
        const correlationId = selectedRemoveCorrelationId;
        const designateLoginId = selectedRemoveDesignateLoginId;

        setActionLoader(true);
        setIsRemoveDesignatedRepModalOpen(false);
        setSelectedRemoveRepName('');
        setSelectedRemoveDesignatorLoginId('');
        setSelectedRemoveCorrelationId('');
        setSelectedRemoveDesignateLoginId('');
        setRemoveFlowType('designatedRep');

        try {
            if (designatorLoginId || correlationId || designateLoginId) {
                const terminateParams = { designatorLoginId, correlationId, designateLoginId };
                await Promise.resolve()
                    .then(() => terminateProxy(terminateParams))
                    .catch((proxyError) => {
                        if (!isNotFoundError(proxyError)) {
                            throw proxyError;
                        }
                    });
            }

            await loadProxyEntitlements();
            if (currentRemoveFlowType === 'sharedAccount') {
                await refreshGuestApprovalState();
                setSharedAccessRefreshKey?.((key: number) => key + 1);
                onSharedActionComplete?.('removed');
            }

            if (repNameToRemove) {
                setRemovedRepNames((prev) => {
                    const normalizedNext = prev.map((name) => name.trim());
                    return normalizedNext.includes(repNameToRemove.trim())
                        ? normalizedNext
                        : [...normalizedNext, repNameToRemove.trim()];
                });
                setProxyList((prev) =>
                    prev.filter(
                        (proxy) =>
                            normalizeRepName(
                                (proxy.humanName as { text?: string; given?: string[]; family?: string } | undefined)?.text ??
                                `${(proxy.humanName as { text?: string; given?: string[]; family?: string } | undefined)?.given?.[0] ?? ''} ${(proxy.humanName as { text?: string; given?: string[]; family?: string } | undefined)?.family ?? ''}`.trim()
                            ) !== normalizeRepName(repNameToRemove)
                    )
                );
            }

            setSuccessModalMessage(
                currentRemoveFlowType === 'sharedAccount'
                    ? t('profile.designatedRepresentative.removeSharedAccessSuccessContent', { fieldName: repNameToRemove })
                    : t('profile.designatedRepresentative.proxyRemovalSuccessAlert')
            );
            setShowSuccessModal(true);
        } catch (error) {
            openErrorModal(error);
        } finally {
            setActionLoader(false);
        }
    }, [
        selectedRemoveRepName,
        removeFlowType,
        selectedRemoveDesignatorLoginId,
        selectedRemoveCorrelationId,
        selectedRemoveDesignateLoginId,
        terminateProxy,
        isNotFoundError,
        openErrorModal,
        contentListingItemClicked,
        loadProxyEntitlements,
        refreshGuestApprovalState,
        setSharedAccessRefreshKey,
        onSharedActionComplete,
    ]);

    useEffect(() => {
        if (!showResendAttemptsAlert) {
            return;
        }

        const hideAlertTimer = setTimeout(() => {
            setShowResendAttemptsAlert(false);
        }, 8000);

        return () => clearTimeout(hideAlertTimer);
    }, [showResendAttemptsAlert, selectedResendRepName, resendAttemptsByRep]);

    // Accounts Shared With Me: confirm a pending request (Approved/Denied), then refresh the table.
    const handleSharedConfirmClick = useCallback(async (
        status: ProxyAuthorizationRequestStatus,
        designatorLoginId?: string,
        correlationId?: string
    ) => {
        trackDrContentClick(
            status === ProxyAuthorizationRequestStatus.Approved
                ? ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_ACCEPT_CLICK
                : ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_DENY_CLICK
        );
        setActionLoader(true);
        try {
            const confirmResponse = await confirmProxyAuthorization({
                proxies: [
                    {
                        personaType: 'Caregiver',
                        identifier: [
                            { system: 'designatorLoginId', value: designatorLoginId ?? '' },
                            { system: 'correlationId', value: correlationId ?? '' },
                        ],
                        Consents: [
                            { ProxyAuthorizationRequestStatus: status },
                        ],
                    },
                ],
                audit: {
                    InitiatedDate: new Date(),
                    ModifiedDate: new Date(),
                },
            });

            if (confirmResponse?.status !== 200) {
                throw (
                    (confirmResponse as { error?: unknown } | undefined)?.error ??
                    confirmResponse ??
                    new Error('Failed to confirm shared account authorization')
                );
            }

            await loadProxyEntitlements();
            await refreshGuestApprovalState();
            onSharedActionComplete?.(
                status === ProxyAuthorizationRequestStatus.Approved
                    ? 'approved'
                    : 'denied'
            );
        } catch (error) {
            openErrorModal(error);
        } finally {
            setSharedAccessRefreshKey?.((key: number) => key + 1);
            setActionLoader(false);
        }
    }, [confirmProxyAuthorization, loadProxyEntitlements, onSharedActionComplete, openErrorModal, refreshGuestApprovalState, setSharedAccessRefreshKey, trackDrContentClick]);

    // Memoized action button configuration
    const actionsBtns = useMemo(
        () => {
            const buildSecondaryAction = (text: string): ActionButtonConfig => ({
                variant: 'secondary_btn',
                textColor: '',
                iconLeft: '',
                iconStroke: '',
                onClick: noop,
                text,
            });

            return {
                update: {
                    ...buildSecondaryAction(t('common.update')),
                    onClick: handleClickAdd,
                },
                resend: buildSecondaryAction(t('common.resend')),
                accept: buildSecondaryAction(t('common.accept')),
                deny: buildSecondaryAction(t('common.deny')),
                remove: {
                    text: t('common.remove'),
                    variant: 'link_btn',
                    textColor: 'error',
                    iconLeft: <TrashIcon />,
                    iconStroke: 'error',
                    onClick: noop,
                },
            };
        },
        [handleClickAdd]
    );

    const getActionsBtns = useCallback(
        (actions: string[], repData?: RepresentativeItem) => {
            const scopedActionButtons = {
                ...actionsBtns,
                update: {
                    ...actionsBtns.update,
                    onClick: () => handleClickAdd(repData),
                    disabled: actionLoader,
                },
                resend: {
                    ...actionsBtns.resend,
                    onClick: () => handleResendClick(
                        repData?.name ?? '',
                        (repData?.correlationId as string) ?? ''
                    ),
                },
                remove: {
                    ...actionsBtns.remove,
                    onClick: () => handleRemoveClick(repData?.name ?? '', 'designatedRep', (repData?.designatorLoginId as string) ?? '', (repData?.correlationId as string) ?? '', (repData?.designateLoginId as string) ?? ''),
                    disabled: actionLoader,
                },
            };

            return renderActionButtons(actions, scopedActionButtons, isDesktop);
        },
        [actionsBtns, isDesktop, handleResendClick, handleClickAdd, handleRemoveClick, actionLoader]
    );

    const getAccountSharedActionsBtns = useCallback(
        (actions: string[], repData?: RepresentativeItem) => {
            const scopedActionButtons = {
                ...actionsBtns,
                accept: {
                    ...actionsBtns.accept,
                    onClick: () => handleSharedConfirmClick(
                        ProxyAuthorizationRequestStatus.Approved,
                        (repData?.designatorLoginId as string) ?? '',
                        (repData?.correlationId as string) ?? ''
                    ),
                    disabled: actionLoader,
                },
                deny: {
                    ...actionsBtns.deny,
                    onClick: () => handleSharedConfirmClick(
                        ProxyAuthorizationRequestStatus.Denied,
                        (repData?.designatorLoginId as string) ?? '',
                        (repData?.correlationId as string) ?? ''
                    ),
                    disabled: actionLoader,
                },
                remove: {
                    ...actionsBtns.remove,
                    onClick: () => handleRemoveClick(repData?.name ?? '', 'sharedAccount', (repData?.designatorLoginId as string) ?? '', (repData?.correlationId as string) ?? '', (repData?.designateLoginId as string) ?? ''),
                    disabled: actionLoader,
                },
            };

            return renderActionButtons(actions, scopedActionButtons, isDesktop);
        },
        [actionsBtns, isDesktop, handleRemoveClick, handleSharedConfirmClick, actionLoader]
    );

    // Normalize each proxy entry from the read API into a common shape for both tabs.
    const normalizedProxies = useMemo(() => {
        return proxyList.map((proxy) => {
            // Read API returns capitalized "Consents"; status/status-read use lowercase "consents".
            const consentsSource = (proxy.Consents ?? proxy.consents) as
                | Array<Record<string, unknown>>
                | undefined;
            const consent = mergeConsents(consentsSource);
            const status =
                ((consent.proxyAuthorizationRequestStatus ??
                    consent.ProxyAuthorizationRequestStatus) as string) ?? '';
            const normalizedStatus = normalizeProxyStatus(status);
            const claimsValue = consent.claims ?? consent.Claims;
            const claimsList = Array.isArray(claimsValue) ? (claimsValue as string[]) : [];
            const claims = formatInfoShared(claimsList);
            const period = (consent.period as { start?: string; end?: string; startDate?: string; endDate?: string } | undefined) ?? {};
            const periodStart = period.startDate ?? period.start;
            const periodEnd = period.endDate ?? period.end;
            const humanName = proxy.humanName as { text?: string; given?: string[]; family?: string } | undefined;
            const name =
                humanName?.text?.trim() ||
                `${humanName?.given?.[0] ?? ''} ${humanName?.family ?? ''}`.trim();
            const designationStatus = (consent.designationStatus as string) ?? '';
            const approveDate = (consent.confirmationDate ?? consent.approveDate) as string | undefined;
            // Proxy email captured for prefilling the update form; not displayed in the table.
            const contactPoints = (proxy.contactPoint as Array<{ system?: string; value?: string }> | undefined) ?? [];
            const email =
                contactPoints.find((cp) => cp.system === 'proxyEmail' || cp.system === 'email')?.value ??
                contactPoints.find((cp) => (cp.value ?? '').includes('@'))?.value ??
                '';
            // Identifiers kept for the terminate API; not displayed.
            const identifiers = (proxy.identifier as Array<{ system?: string; value?: string }> | undefined) ?? [];
            const designatorLoginId =
                getIdentifierBySystem(identifiers, 'designatorLoginId') ||
                (proxy.designatorLoginId as string | undefined) ||
                '';
            const correlationId =
                getIdentifierBySystem(identifiers, 'correlationId') ||
                (proxy.correlationId as string | undefined) ||
                '';
            const designateLoginId =
                getIdentifierBySystem(identifiers, 'designateLoginId') ||
                (proxy.designateLoginId as string | undefined) ||
                '';
            // Purpose of disclosure captured for prefilling the update form; not displayed in the table.
            const disclosureOption = (proxy.DisclosureOption ?? proxy.disclosureOption) as string | undefined;
            const disclosureOptionDescription = (proxy.DisclosureOptionDescription ?? proxy.disclosureOptionDescription) as string | undefined;
            // Substance-use authorization captured for prefilling the update form; not displayed in the table.
            const alcoholAbuseAuthorization = (proxy.AlcoholAbuseAuthorization ?? proxy.alcoholAbuseAuthorization) as string | undefined;

            return {
                name,
                claims,
                claimsList,
                email,
                status: normalizedStatus,
                designationStatus,
                dateApproved: formatSharedApproveDate(approveDate),
                initiatedDate: periodStart ? formatISODateToMDY(periodStart) : '',
                expirationDate: periodEnd ? formatISODateToMDY(periodEnd) : '',
                expirationStartRaw: periodStart ?? '',
                expirationEndRaw: periodEnd ?? '',
                designatorLoginId: designatorLoginId ?? '',
                correlationId: correlationId ?? '',
                designateLoginId: designateLoginId ?? '',
                disclosureOption: disclosureOption ?? '',
                disclosureOptionDescription: disclosureOptionDescription ?? '',
                alcoholAbuseAuthorization: alcoholAbuseAuthorization ?? '',
            };
        });
    }, [proxyList]);

    // "My Designated Representatives": every entry except accounts shared with me; hidden statuses are excluded.
    const designatedRepsFromApi = useMemo(() => {
        return normalizedProxies
            .filter((proxy) => proxy.designationStatus !== 'Designate' && !MY_REP_HIDDEN_STATUSES.has(proxy.status))
            .map((proxy) => {
                const rep = {
                    name: proxy.name,
                    infoShared: proxy.claims,
                     dateApproved: MEMBER_VIEW_EMPTY_APPROVE_DATE_STATUSES.has(proxy.status)
                        ? '-'
                        : proxy.dateApproved,
                    status: <Badge status={MY_REP_STATUS_DISPLAY[proxy.status] ?? proxy.status} className="rounded-pill" />,
                    initiatedDate: proxy.initiatedDate,
                    expirationDate: proxy.expirationDate,
                    timestamp: new Date().toISOString(),
                    designatorLoginId: proxy.designatorLoginId,
                    correlationId: proxy.correlationId,
                    designateLoginId: proxy.designateLoginId,
                    email: proxy.email,
                    claimsList: proxy.claimsList,
                    expirationStartRaw: proxy.expirationStartRaw,
                    expirationEndRaw: proxy.expirationEndRaw,
                    disclosureOption: proxy.disclosureOption,
                    disclosureOptionDescription: proxy.disclosureOptionDescription,
                    alcoholAbuseAuthorization: proxy.alcoholAbuseAuthorization,
                    actionKeys: STATUS_ACTION_KEYS[proxy.status] ?? [],
                };
                return { ...rep, actions: getActionsBtns(rep.actionKeys, rep) };
            });
    }, [normalizedProxies, getActionsBtns]);

    // "Accounts Shared With Me": designations where the logged-in user is the designate; only statuses with actions (Approved, Pending) are shown.
    const accountSharedRepsFromApi = useMemo(() => {
        return normalizedProxies
            .filter((proxy) => proxy.designationStatus === 'Designate' && Boolean(SHARED_STATUS_ACTION_KEYS[proxy.status]))
            .map((proxy) => {
                const sharedActionKeys = SHARED_STATUS_ACTION_KEYS[proxy.status] ?? [];
                const rep = {
                    name: proxy.name,
                    infoCanAccessWeb: proxy.claims,
                    dateApproved: PROXY_VIEW_EMPTY_APPROVE_DATE_STATUSES.has(proxy.status)
                        ? '-'
                        : proxy.dateApproved,
                    designatorLoginId: proxy.designatorLoginId,
                    correlationId: proxy.correlationId,
                    designateLoginId: proxy.designateLoginId,
                    actionKeys: sharedActionKeys,
                };
                return { ...rep, actions: getAccountSharedActionsBtns(sharedActionKeys, rep) };
            });
    }, [normalizedProxies, getAccountSharedActionsBtns]);

    // Use provided data (tests) or fall back to the proxies returned by the read API.
    const designatedReps =
        (designatedRepsData as typeof designatedRepsFromApi | undefined) ??
        designatedRepsFromApi;
    const accountSharedReps =
        (accountSharedRepsData as typeof accountSharedRepsFromApi | undefined) ??
        accountSharedRepsFromApi;

    const filteredDesignatedReps = designatedReps.filter(
        (rep) => !removedRepNames.some((name) => normalizeRepName(name) === normalizeRepName(rep.name))
    );
    const filteredAccountSharedReps = accountSharedReps.filter(
        (rep) => !removedRepNames.some((name) => normalizeRepName(name) === normalizeRepName(rep.name))
    );

    // derive resend alert state
    const selectedResendAttempts = selectedResendRepName
        ? getAttemptsForRep(selectedResendRepName)
        : 0;

    // Derived in one memo so the branching stays out of the component's cognitive complexity.
    const { shouldShowResendAlert, resendAlertMessage } = useMemo(() => {
        const show = showResendAttemptsAlert && !!selectedResendRepName;

        const message = t('profile.designatedRepresentative.attemptsRemainingMsg', {
            attempts: selectedResendAttempts,
            repName: selectedResendRepName,
        });

        return { shouldShowResendAlert: show, resendAlertMessage: message };
    }, [showResendAttemptsAlert, selectedResendAttempts, selectedResendRepName]);

    // generate table headers dynamically based on the keys
    const getHeadersFromObject = useCallback(
        (data?: typeof designatedReps | typeof accountSharedReps) => {
            const normalizedSource = data ?? filteredDesignatedReps;
            const keys = Object.keys(normalizedSource[0] || {}) as string[];
            return keys.filter((key) => shouldIncludeHeaderKey(key, isDesktop));
        },
        [filteredDesignatedReps, isDesktop]
    );

    const headers = getHeadersFromObject();
    const accountSharedHeaders = getHeadersFromObject(filteredAccountSharedReps);

    const getTranslatedHeaders = useCallback((headerKeys: string[]) => {
        return headerKeys.map((key) => ({ text: t(resolveHeaderTranslationKey(key)) }));
    }, []);

    const headerElements = getTranslatedHeaders(headers);
    const accountSharedHeaderElements = getTranslatedHeaders(accountSharedHeaders);

    // generate table rows dynamically based on the object values
    const getTableRowsFromObject = useCallback(
        (
            data: typeof designatedReps | typeof accountSharedReps,
            rowHeaders: string[]
        ) => {
            return data.map((rep, index) => {
                const hasMultipleActions = rep.actionKeys.length > 1;
                const rowData = rowHeaders.map((header) => {
                    if (header === 'actions') {
                        return { text: rep[header as keyof typeof rep] as React.ReactNode };
                    }
                    return { text: rep[header as keyof typeof rep] as string };
                });
                return (
                    <tr key={`rep-row-${index}`}>
                        {rowData.map((data, idx) => {
                            const cellClassName =
                                hasMultipleActions && rowHeaders[idx] !== 'actions'
                                    ? 'align-top'
                                    : '';
                            if (isDesktop && idx === 0) {
                                return (
                                    <th
                                        key={`rep-${index}-data-${idx}`}
                                        scope="row"
                                        className={cellClassName}
                                    >
                                        {data.text}
                                    </th>
                                );
                            }

                            return (
                                <td key={`rep-${index}-data-${idx}`} className={cellClassName}>
                                    {data.text}
                                </td>
                            );
                        })}
                    </tr>
                );
            });
        },
        [isDesktop]
    );

    const getAccordionItemsFromObject = useCallback(
        (data: typeof designatedReps | typeof accountSharedReps) => {
            return data.map((rep) => {
                const repKeys = Object.keys(rep).filter(shouldIncludeAccordionKey);
                const actionColSpan = 2;

                return {
                    header: rep.name,
                    body: (
                        <>
                            {repKeys.map((key) => (
                                <tr key={`${rep.name}-${key}`}>
                                    <th scope="row">{t(resolveHeaderTranslationKey(key))}:</th>
                                    <td>{rep[key as keyof typeof rep]}</td>
                                </tr>
                            ))}
                            <tr>
                                <th scope="row">{t(resolveHeaderTranslationKey('actions'))}:</th>
                                <td colSpan={actionColSpan}>{rep.actions}</td>
                            </tr>
                        </>
                    ),
                };
            });
        },
        [isDesktop]
    );

    const accordionItems = getAccordionItemsFromObject(filteredDesignatedReps);
    const accountSharedAccordionItems = getAccordionItemsFromObject(filteredAccountSharedReps);

    //mock tabs work in progress, will replace with API data in the future
    const tabs = {
        tab1: {
            id: 'tab1',
            tabName: t('profile.designatedRepresentative.DRDropDownMenuItem1'),
            total: filteredDesignatedReps.length.toString(),
            content: (
                <div className="row mb-4">
                    {initialLoader ? (
                        <div className="col-12 text-center py-5">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            {shouldShowResendAlert ? (
                                <div className="col-12 mb-2">
                                    <DetailedAlertMessages
                                        message={resendAlertMessage}
                                        type="info"
                                        variant="warning"
                                    />
                                </div>
                            ) : null}
                            {filteredDesignatedReps.length > 0 ? (
                                <div className="col-12 order-xl-0 order-1">
                                    <Table
                                        className="table lg-content-table"
                                        header={headerElements}
                                        headerBgColor="secondary"
                                        headerTextColor="primary"
                                        verticalAlign="top"
                                        rowsChildren={getTableRowsFromObject(filteredDesignatedReps, headers)}
                                        firstColumnWidth="9.75rem"
                                        secondColumnWidth="12rem"
                                        isAccordionTable={!isDesktop}
                                        accordionItems={accordionItems}
                                    />
                                </div>
                            ) : null}
                            <div
                                className={`col-xl-7 col-12 align-self-center ${filteredDesignatedReps.length > 0 ? 'order-xl-1 order-0' : ''
                                    }`}
                            >
                                <Paragraph
                                    text={
                                        <Trans
                                            i18nKey={t(
                                                `profile.designatedRepresentative.${filteredDesignatedReps.length
                                                    ? 'designatedRepTabContent'
                                                    : 'designatedRepTabContentDefault'
                                                }`
                                            )}
                                        />
                                    }
                                    variant="regular"
                                    dataTestId={'designatedRepresentativeSection'}
                                    className="mb-xl-0 lineBrake"
                                />
                            </div>
                            <div
                                className={`col-xl-5 col-12 mt-xl-auto mt-3 text-end ${filteredDesignatedReps.length > 0 ? 'order-2' : ''
                                    }`}
                            >
                                <Button
                                    dataTestId={`${t('profile.designatedRepresentative.addDesignatedRepBtn')} Button`}
                                    variant="primary_btn"
                                    onClick={() => handleClickAdd()}
                                >
                                    {t('profile.designatedRepresentative.addDesignatedRepBtn')}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            ),
        },
        tab2: {
            id: 'tab2',
            tabName: t('profile.designatedRepresentative.DRDropDownMenuItem2'),
            total: filteredAccountSharedReps.length.toString(),
            content: (
                <div className="row mb-4">
                    {initialLoader ? (
                        <div className="col-12 text-center py-5">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            {filteredAccountSharedReps.length > 0 ? (
                                <div className="col-12 order-xl-0 order-1">
                                    <Table
                                        className="table lg-content-table"
                                        header={accountSharedHeaderElements}
                                        headerBgColor="secondary"
                                        headerTextColor="primary"
                                        verticalAlign="top"
                                        rowsChildren={getTableRowsFromObject(
                                            filteredAccountSharedReps,
                                            accountSharedHeaders
                                        )}
                                        isAccordionTable={!isDesktop}
                                        firstColumnWidth="20%"
                                        secondColumnWidth="35%"
                                        lastColumnWidth="11rem"
                                        accordionItems={accountSharedAccordionItems}
                                    />
                                </div>
                            ) : null}
                            <div
                                className={`col-12 ${filteredAccountSharedReps.length ? 'order-xl-1 order-0' : ''
                                    }`}
                            >
                                <Paragraph
                                    text={
                                        <Trans
                                            i18nKey={t(
                                                `profile.designatedRepresentative.${filteredAccountSharedReps.length
                                                    ? 'accountsSharedTabContents'
                                                    : 'accountSharedDescription2'
                                                }`
                                            )}
                                            values={{ brandName: t('common.brandName') }}
                                            components={{ bold: <strong /> }}
                                        />
                                    }
                                    variant="regular"
                                    dataTestId={'designatedRepresentativeSection'}
                                    className="mb-xl-0 lineBrake"
                                />
                            </div>
                        </>
                    )}
                </div>
            ),
        },
    };

    const isSharedOnlyView = viewMode === 'sharedOnly';

    const renderTabs = () => {
        if (isSharedOnlyView) {
            return tabs.tab2.content;
        }

        return (
            <>
                <Tabs
                    isMobile={!isDesktop}
                    tabs={Object.keys(tabs).map((key, index) => {
                        const tab = tabs[key as keyof typeof tabs];
                        return (
                            <TabList
                                isMobile={!isDesktop}
                                key={index}
                                tabId={key}
                                tabName={tab.tabName}
                                total={tab.total}
                                isActive={isActiveTab === key}
                                dataTestId={`${key}-tab`}
                                onClick={() => {
                                    setIsActiveTab(key);
                                }}
                            />
                        );
                    })}
                    tabsContent={Object.keys(tabs).map((key, index) => {
                        const tab = tabs[key as keyof typeof tabs];
                        return (
                            <TabContent
                                key={index}
                                tabId={key}
                                tabContent={tab?.content}
                                dataTestId={`${key}-tabContent`}
                                isActive={isActiveTab === key}
                            />
                        );
                    })}
                />
            </>
        );
    };

    const renderedContent = (
        <div className="set_my_preferences">
            <div className="user_address_wrapper py-4 border_wrapper">
                <ProfileHeaderContent
                    header={''}
                    contentOne={''}
                    contentOrObject={renderTabs()}
                    dataTestIdOne="designatedRepresentativeDescription"
                />
                {showError && (
                    <div
                        data-testid="designated-representative-error-placeholder"
                        className="d-none"
                        aria-hidden="true"
                    />
                )}
            </div>
        </div>
    );

    const isSharedAccountRemoval = removeFlowType === 'sharedAccount';

    const sharedContent = (
        <>
            {renderedContent}
            {/* Remove designated representative confirmation modal */}
            {isRemoveDesignatedRepModalOpen && (
                <ModalMultiUse
                    isOpen={isRemoveDesignatedRepModalOpen}
                    onClose={onCloseRemove}
                    header={t(isSharedAccountRemoval ? 'profile.designatedRepresentative.removeSharedAccessTitle' : 'profile.designatedRepresentative.proxyRemovalTitle')}
                    btnClose={false}
                    variant="defaultNoPyBody"
                    body={
                        <>
                            <Paragraph
                                text={isSharedAccountRemoval
                                    ? t('profile.designatedRepresentative.proxySelfRemovalDescription', { fieldName: selectedRemoveRepName })
                                    : t('profile.designatedRepresentative.removeDesignatedRepresentativeDescription', { designatedRepresentative: selectedRemoveRepName })}
                                variant="regular"
                                className="lineBrake mb-0"
                            />
                            {listType(
                                isSharedAccountRemoval
                                    ? t('profile.designatedRepresentative.removeSharedAccessDescList', { fieldName: selectedRemoveRepName })
                                    : t('profile.designatedRepresentative.removeDesignatedRepDescList', { fieldName: selectedRemoveRepName }),
                                'lineBrake discList'
                            )}
                        </>
                    }
                    footer={
                        <>
                            <div className="col-md-6 col-12 mb-md-0 mb-3 ps-sm-0 pe-sm-2 px-0">
                                <Button
                                    dataTestId={'proxyRemovalPrimaryButtonLabel'}
                                    variant="primary_btn"
                                    className="btn-md-height"
                                    type="submit"
                                    onClick={onConfirmRemove}
                                    disabled={actionLoader}
                                >
                                    {t(isSharedAccountRemoval ? 'profile.designatedRepresentative.yesDenyRequest' : 'profile.designatedRepresentative.proxyRemovalPrimaryButtonLabel')}
                                </Button>
                            </div>
                            <div className="col-md-6 col-12 ps-sm-2 pe-sm-0 px-0">
                                <Button
                                    dataTestId={'cancelRemoveDesignatedRepBtn'}
                                    variant="secondary_btn"
                                    className="btn-md-height w-100"
                                    onClick={onCloseRemove}
                                >
                                    {t('common.cancel')}
                                </Button>
                            </div>
                        </>
                    }
                    data-testid="removeDesignatedRepModal"
                />
            )}
            {showErrorModal && (
                <ModalMultiUse
                    isOpen={showErrorModal}
                    onClose={closeErrorModal}
                    header={errorModalTitle || t('common.error')}
                    variant="default"
                    body={(
                        <Paragraph
                            text={errorModalMessage}
                            variant="regular"
                            className="lineBrake mb-0"
                        />
                    )}
                    footer={(
                        <div className="col-12 px-0">
                            <Button
                                dataTestId={'closeRemoveDesignatedRepErrorBtn'}
                                variant="primary_btn"
                                className="btn-md-height"
                                onClick={closeErrorModal}
                            >
                                {t('common.close')}
                            </Button>
                        </div>
                    )}
                    data-testid="removeDesignatedRepErrorModal"
                />
            )}
            {showSuccessModal && (
                <ModalMultiUse
                    isOpen={showSuccessModal}
                    onClose={closeSuccessModal}
                    header={t('profile.designatedRepresentative.proxyRemovalSuccessTitle')}
                    btnClose={false}
                    variant="defaultNoPyBody"
                    body={(
                        <Paragraph
                            text={successModalMessage}
                            variant="regular"
                            className="lineBrake mb-0"
                        />
                    )}
                    footer={(
                        <div className="col-12 px-0">
                            <Button
                                dataTestId={'closeRemoveDesignatedRepSuccessBtn'}
                                variant="primary_btn"
                                className="btn-md-width w-100"
                                onClick={closeSuccessModal}
                            >
                                {t('common.btnOk')}
                            </Button>
                        </div>
                    )}
                    data-testid="removeDesignatedRepSuccessModal"
                />
            )}
        </>
    );

    if (!showCollapsible) {
        return sharedContent;
    }

    return (
        <Collapsible
            title={t('profile.designatedRepresentative.designatedRepresentativeTab')}
            id={'designatedRepresentative'}
            isMobile={isMobile}
            key={`${'designatedRepresentative'}-${isMobile ? 'mobile' : 'desktop'}`}
            expand={expand}
            onExpand={onExpand}
            onCollapse={onCollapse}
            className="my-3 pb-2"
        >
            {sharedContent}
        </Collapsible>
    );
};

export default DesignatedRepresentative;